import { PersonAdd } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  LinearProgress,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { supabaseGetter } from '~/api/supabase-getter'
import { useCurrentUser } from '~/common/use-current-user'
import { useFeatures } from '~/common/use-features'
import { InviteUserModal } from '~/invite-user/invite-user-modal'

const ROLE_COLOR = {
  owner: 'primary',
  admin: 'secondary',
  member: 'default',
}

const COLS = '32px 1.5fr 1fr 1fr 90px'

const HeaderCell = ({ children, align = 'left' }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap sx={{ textAlign: align }}>
    {children}
  </Typography>
)

export const FarmMembersCard = ({ farmId }) => {
  const [members, setMembers] = useState([])
  const [loading, setLoading] = useState(true)
  const [isInviteUserModalOpen, setIsInviteUserModalOpen] = useState(false)
  const { currentUser } = useCurrentUser()
  const { features } = useFeatures()

  useEffect(() => {
    async function fetchMembers() {
      if (!farmId) { setLoading(false); return }
      setLoading(true)
      const { data, error } = await supabaseGetter.organizationUsers.getByOrganizationWithUsers(farmId)
      if (!error && data) setMembers(data)
      setLoading(false)
    }
    fetchMembers()
  }, [farmId])

  const isOwner = members.some(m => m.user_id === currentUser?.id && m.role === 'owner')
  const canInvite = features['invite_users'] && isOwner

  return (
    <Card variant="outlined">
      {canInvite && (
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 1,
            }}
          >
            <Button variant="outlined" size="small" startIcon={<PersonAdd />} onClick={() => setIsInviteUserModalOpen(true)}>
              Add Member
            </Button>
          </Box>
        </Box>
      )}
      <CardHeader title="Members" sx={{ pb: 0 }} />
      <CardContent sx={{ overflowX: 'auto', px: { xs: 1, sm: 2 } }}>
        {loading && <LinearProgress />}
          
        {members.length > 0 && (
          <Box sx={{ minWidth: 480 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: COLS,
                alignItems: 'center',
                gap: 2,
                py: 0.5,
                borderBottom: 1,
                borderColor: 'divider',
                mb: 0.5,
              }}
            >
              <Box />
              <HeaderCell>User</HeaderCell>
              <HeaderCell align="right">Added</HeaderCell>
              <HeaderCell align="right">Updated</HeaderCell>
              <HeaderCell align="right">Role</HeaderCell>
            </Box>
            {members.map((m) => {
              const user = m.users || {}
              const initials = user.username
                ? user.username.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
                : '?'
              return (
                <Box
                  key={m.user_id}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: COLS,
                    alignItems: 'center',
                    gap: 2,
                    py: 0.75,
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 0 },
                  }}
                >
                  <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>{initials}</Avatar>
                  <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                    <Typography variant="body2" noWrap>{user.username || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>{user.email || '—'}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ textAlign: 'right' }}>
                    {user?.created_at
                      ? new Date(user.created_at).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                      : '—'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ textAlign: 'right' }}>
                    {user?.updated_at
                      ? new Date(user.updated_at).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                      : '—'}
                  </Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Chip
                      label={m.role}
                      size="small"
                      color={ROLE_COLOR[m.role] || 'default'}
                    />
                  </Box>
                </Box>
              )
            })}
          </Box>
        )}
      </CardContent>

      <InviteUserModal open={isInviteUserModalOpen} onClose={() => setIsInviteUserModalOpen(false)} farmId={farmId} />
    </Card>
  )
}
